import { useMemo } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

import AdminDeleteClinicListTemplate from '@/components/templates/AdminDeleteClinicListTemplate';
import { useAdminDeleteClinicList } from '@/hooks/admin/useAdminDeleteClinicList';
import { useRestoreDeleteClinic } from '@/hooks/admin/useRestoreDeleteClinic';
import { useRedirectWhenPageEmpty } from '@/hooks/useRedirectWhenPageEmpty';
import { useTenantSiteTypes } from '@/hooks/admin/useTenantSiteTypes';
import type { DeleteClinic } from '@/types/admin-delete-clinic';

const PAGE_SIZE = 20;

const SiteAdminDeleteClinicsPage = () => {
  const { site } = useParams<{ site: string }>();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page') || '1', 10) || 1;

  const isSuperAdminRoute = pathname.startsWith('/super-admin/admin');
  const canShowSiteType = isSuperAdminRoute || site === 'medix';

  const { data, isLoading, isError } = useAdminDeleteClinicList({
    page,
    per_page: PAGE_SIZE,
  });
  const { restoreClinic } = useRestoreDeleteClinic();

  const siteTypesQuery = useTenantSiteTypes(site);
  const tenantNameBySiteType = useMemo(() => {
    const map = new Map<string, string>();
    (siteTypesQuery.data?.data?.site_types ?? []).forEach(t => {
      if (t.site_type) map.set(t.site_type, t.name);
    });
    return map;
  }, [siteTypesQuery.data]);

  const listData = data?.data ?? [];

  useRedirectWhenPageEmpty({
    searchParams,
    setSearchParams,
    isLoading,
    isError,
    dataLength: listData.length,
  });

  const breadcrumbItems = [{ title: '削除治療院' }];

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  const handleRestoreClick = async (record: DeleteClinic) => {
    await restoreClinic(record.id);
  };

  return (
    <AdminDeleteClinicListTemplate
      data={listData}
      total={data?.meta.total ?? 0}
      current={data?.meta.current_page ?? 1}
      pageSize={data?.meta.per_page ?? PAGE_SIZE}
      loading={isLoading}
      breadcrumbItems={breadcrumbItems}
      onPageChange={handlePageChange}
      onRestoreClick={handleRestoreClick}
      tenantNameBySiteType={tenantNameBySiteType}
      showSiteTypeColumn={canShowSiteType}
    />
  );
};

export default SiteAdminDeleteClinicsPage;
